import { forwardRef, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn, Input } from '../../components/ui';

/**
 * Phone-first pieces shared by the customer app pages: loading placeholders shaped like what loads into them (so the
 * page does not jump when the data arrives), the PIN field, and the bar that keeps a page's main action under the thumb.
 */

/** A grey block with the shape of the text or number that loads into it. */
export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cn('animate-pulse rounded-md bg-slate-200', className)} />;
}

/** Placeholder for a list of cards or rows while it loads (role=status, so screen readers hear "Loading"). */
export function ListSkeleton({ rows = 3, label = 'Loading', className }: { rows?: number; label?: string; className?: string }) {
  return (
    <div role="status" aria-label={label} className={cn('flex flex-col gap-2', className)}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <Skeleton className="h-5 w-16 shrink-0" />
        </div>
      ))}
    </div>
  );
}

/** Placeholder for the balance card at the top of Home and Wallet: the same size, so nothing below it moves. */
export function BalanceSkeleton({ label = 'Loading your balance' }: { label?: string }) {
  return (
    <div role="status" aria-label={label} className="flex h-36 flex-col justify-center gap-3 rounded-2xl bg-gradient-to-br from-brand-700 to-brand-900 p-5 opacity-80">
      <div aria-hidden className="h-4 w-24 animate-pulse rounded bg-white/30" />
      <div aria-hidden className="h-9 w-40 animate-pulse rounded bg-white/30" />
      <div aria-hidden className="h-3 w-48 animate-pulse rounded bg-white/20" />
    </div>
  );
}

/**
 * Keeps a page's main action under the thumb: it sits in the page's flow, and while the page is longer than the
 * screen it stays pinned just above the bottom tab bar (and above the iPhone home indicator).
 */
export function ThumbBar({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('sticky bottom-[calc(4.75rem+env(safe-area-inset-bottom))] z-10', className)}>{children}</div>;
}

type PinInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> & {
  value: string;
  onChange: (digits: string) => void;
};

/**
 * The PIN: one large numeric field (the phone shows its number pad), digits only, with a Show/Hide button so the
 * customer can check what they typed. Pass `id` (from Field) or `aria-label` for its name.
 */
export const PinInput = forwardRef<HTMLInputElement, PinInputProps>(function PinInput({ value, onChange, maxLength = 6, className, ...rest }, ref) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input
        ref={ref}
        {...rest}
        type={show ? 'text' : 'password'}
        inputMode="numeric"
        pattern="[0-9]*"
        enterKeyHint="done"
        maxLength={maxLength}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, maxLength))}
        className={cn('h-14 px-14 text-center text-2xl tracking-[0.4em] tabular lg:h-14', className)}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        aria-label={show ? 'Hide PIN' : 'Show PIN'}
        aria-pressed={show}
        className="absolute inset-y-0 right-0 flex w-14 items-center justify-center rounded-r-lg text-slate-500 hover:text-slate-800"
      >
        <svg aria-hidden viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
          <circle cx="12" cy="12" r="3" />
          {show && <path d="M4 4l16 16" />}
        </svg>
      </button>
    </div>
  );
});
